import { useCallback } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { useOrderConversation } from '@/hooks/use-order-conversation';
import { FBMessage } from '@/types/fb-type';
import { Customer } from '@/types/customer-type';

interface UseCustomerChatResult {
  canChat: boolean;
  currentConversation: ReturnType<typeof useOrderConversation>['currentConversation'];
  messages: FBMessage[];
  isLoadingMessage: boolean;
  prevCursor: string | null;
  onNewMessage?: (m: FBMessage) => void;
  onLoadMoreMessage?: (after: string) => void;
}

export function useCustomerChat(customer: Customer | undefined): UseCustomerChatResult {
  const { shouldShowChat, currentConversation } = useOrderConversation(
    customer || null,
    undefined,
    {
      enabled: Boolean(customer),
      showMissingChatWarning: false,
    },
  );
  const { messages, isLoadingMessage, messageAfterCursor, onLoadMoreMessage, onMessage } =
    useChat();

  // Handle new messages
  const handleNewMessage = useCallback(
    (message: FBMessage) => {
      onMessage(message);
    },
    [onMessage],
  );

  // Handle loading more messages
  const handleLoadMoreMessage = useCallback(
    (after: string) => {
      if (onLoadMoreMessage && after) {
        onLoadMoreMessage(after);
      }
    },
    [onLoadMoreMessage],
  );

  // Determine if chat is available
  const canChat = Boolean(
    shouldShowChat &&
      currentConversation &&
      (customer?.facebookId || customer?.facebookConversationId),
  );

  return {
    canChat,
    currentConversation,
    messages,
    isLoadingMessage,
    prevCursor: messageAfterCursor,
    onNewMessage: handleNewMessage,
    onLoadMoreMessage: handleLoadMoreMessage,
  };
}
